import { useTranslation } from 'react-i18next';
import { Button, Tooltip, ScrollShadow } from '@heroui/react';
import { useDataStore } from '@/stores/dataStore';
import { emitAppEvent } from '@/lib/appEvents';
import { SettingsIcon, FolderIcon, SearchIcon, UserPlusIcon, AlarmClockIcon } from '@/components/icons';
import { HeroChannelList } from './HeroChannelList';
import { HeroDmList } from './HeroDmList';
import { HeroSidebarFooter } from './HeroSidebarFooter';
/**
 * Left column of the HeroUI shell: a HeroUI `surface` aside with a header,
 * a scrollable body (guild channels or the DM list) and the user widget.
 * In a guild the header carries a gear that opens the (skin-aware)
 * ServerSettingsModal — the HeroUI shell's entry point for server admin,
 * mirroring the Aether ChannelDrawer. Pure HeroUI presentation wired to
 * the shared dataStore.
 */
export function HeroSidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { t } = useTranslation();
  const guildId = useDataStore((s) => s.currentGuildId);
  const guild = useDataStore((s) => (guildId ? s.guilds[guildId] : undefined));
  return (
    <aside className="studio-sidebar flex h-full min-h-0 w-full min-w-0 shrink-0 flex-col border-r border-separator bg-surface">
      <div className="flex h-16 shrink-0 items-center gap-2 border-b border-separator px-4">
        <h2 className="studio-sidebar-title min-w-0 flex-1 truncate text-base font-semibold text-foreground">
          {guild ? guild.name : t('dm.conversations')}
        </h2>
        {guild ? (
          <Tooltip delay={0}>
              <Button aria-label={t('sidebar.serverSettings')} isIconOnly size="sm" variant="ghost" className="group" onPress={() => emitAppEvent('guild:settings', { tab: 'overview' })}>
                <SettingsIcon className="size-[18px] motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out motion-safe:group-hover:rotate-[360deg]" />
              </Button>
            <Tooltip.Content>{t('sidebar.serverSettings')}</Tooltip.Content>
          </Tooltip>
        ) : (
          <div className="flex items-center gap-1">
            <Tooltip delay={0}>
                <Button aria-label={t('ui2.peopleSearch.title')}
                  data-tour="add-friends"
                  isIconOnly
                  size="sm"
                  variant="ghost"
                  className="group"
                  onPress={() => emitAppEvent('people:open')}
                >
                  <UserPlusIcon className="size-[18px] transition-transform group-hover:scale-110" />
                </Button>
              <Tooltip.Content>{t('ui2.peopleSearch.title')}</Tooltip.Content>
            </Tooltip>
            <Tooltip delay={0}>
                <Button aria-label={t('reminders.title')}
                  isIconOnly
                  size="sm"
                  variant="ghost"
                  className="group"
                  onPress={() => emitAppEvent('reminders:open')}
                >
                  <AlarmClockIcon className="size-[18px] transition-transform group-hover:scale-110" />
                </Button>
              <Tooltip.Content>{t('reminders.title')}</Tooltip.Content>
            </Tooltip>
            <Tooltip delay={0}>
                <Button aria-label={t('sidebar.folders')}
                  isIconOnly
                  size="sm"
                  variant="ghost"
                  className="group"
                  onPress={() => emitAppEvent('dm-folders:manage')}
                >
                  <FolderIcon className="size-[18px] transition-transform group-hover:scale-110" />
                </Button>
              <Tooltip.Content>{t('sidebar.folders')}</Tooltip.Content>
            </Tooltip>
          </div>
        )}
      </div>
      {!guild && <button type="button" onClick={() => emitAppEvent('search:open')} className="studio-search">
        <SearchIcon className="size-4 shrink-0" />
        <span className="truncate">{t('sidebar.searchMessages')}</span>
      </button>}
      <ScrollShadow hideScrollBar className="min-h-0 flex-1 p-2">
        {guild ? <HeroChannelList guild={guild} onNavigate={onNavigate} /> : <HeroDmList />}
      </ScrollShadow>
      <HeroSidebarFooter onOpenSettings={() => emitAppEvent('settings:open', {})} />
    </aside>
  );
}
